/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

class VariantSpray extends HTMLElement {
  connectedCallback() {
    this.formElement = document.getElementById(this.getAttribute('data-target'));
    this.quantitySelectors = this.querySelectorAll('quantity-wrapper');
    if (this.formElement) {
      this.addEventListener('change', this.handleChange.bind(this));
    }
  }

  handleChange() {
    let items = [];

    this.quantitySelectors.forEach((quantitySelector) => {
      let variantId = quantitySelector.dataset.variant;
      let qty = parseInt(quantitySelector.querySelector('input').value);
      if (qty > 0) {
        let item = {
          id: variantId,
          quantity: qty,
        };
        items.push(item);
      }
    });
    let formElements = '';
    items.forEach((item, index) => {
      formElements += `<input type="hidden" name="items[${index}][id]" value="${item.id}">
                            <input type="hidden" name="items[${index}][quantity]" value="${item.quantity}">`;
    });
    let formNode = document.createElement('div');
    formNode.setAttribute('data-multi', true);
    formNode.innerHTML = formElements;
    this.deleteDefinedVariant();
    this.formElement.appendChild(formNode);
  }

  deleteDefinedVariant() {
    let variantInput = this.formElement.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.remove();
    }

    //Div which contains our hidden values
    let valueDiv = this.formElement.querySelector('[data-multi]');
    if (valueDiv) {
      valueDiv.remove();
    }
  }
}
window.customElements.define('variant-spray', VariantSpray);

class SizeChart {
  constructor(sizeChart, domElement) {
    this.sizeChart = sizeChart;
    this.domElement = domElement;
  }

  initSizeChart() {
    if (!this.sizeChart) {
      return;
    }
    const sizchartVals = JSON.parse(this.sizeChart?.getAttribute('data-sizechart-vals'));
    this.euVals = sizchartVals.eur_chart_values || sizchartVals.eu_chart_values || [];
    this.usVals = sizchartVals.us_chart_values;
    this.ukVals = sizchartVals.uk_chart_values;
    this.cmVals = sizchartVals.cm_chart_values;
    this.standardVals = sizchartVals.standard_size_chart_values;
    this.sizeSelector = this.domElement.querySelector('[data-size]');

    if (this.sizeSelector) {
      this.USsizes = {};
      this.UKsizes = {};
      this.CMsizes = {};
      this.StandardSizes = {};
      const inputValues = this.sizeSelector.querySelectorAll('input');
      inputValues.forEach((valueItem) => {
        const euValue = valueItem.value;
        if (this.findIndex(this.euVals, euValue) < 0) {
          return;
        }

        const usValue = this.findFirstConversionValue(euValue, this.usVals);
        const ukValue = this.findFirstConversionValue(euValue, this.ukVals);
        const cmValue = this.findFirstConversionValue(euValue, this.cmVals);
        const standardValue = this.findFirstConversionValue(euValue, this.standardVals);

        if (usValue !== null) {
          this.USsizes[euValue] = usValue;
        }
        if (ukValue !== null) {
          this.UKsizes[euValue] = ukValue;
        }
        if (cmValue !== null) {
          this.CMsizes[euValue] = cmValue;
        }
        if (standardValue !== null) {
          this.StandardSizes[euValue] = standardValue;
        }
      });

      [
        [this.USsizes, 'us'],
        [this.UKsizes, 'uk'],
        [this.CMsizes, 'cm'],
        [this.StandardSizes, 'standard'],
      ].forEach(([sizes, unit]) => {
        for (const [key, value] of Object.entries(sizes)) {
          const input = this.getInputByEuValue(key);
          if (!input) {
            continue;
          }

          const label = this.getAliasElement(input, unit);
          if (label && value) {
            const labelText = label.querySelector('span');
            if (labelText) {
              labelText.innerText = value;
            }
          }
        }
      });
    }
    this.initChartSelector();
  }

  initChartSelector() {
    this.chartContainer = this.domElement.querySelector('[data-tabs="size--charts"]');
    if (this.chartContainer) {
      this.chartTabButtons = this.chartContainer.querySelectorAll('[data-size-label]');
      this.chartTabButtons.forEach((button) => {
        button.addEventListener('click', this.handleTabClick.bind(this));
      });
    }
  }

  handleTabClick(evt) {
    if (evt.currentTarget.classList.contains('disabled')) {
      evt.preventDefault();
      return;
    }

    this.chartTabButtons.forEach((button) => {
      button.classList.remove('active');
    });
    evt.currentTarget.classList.add('active');
    this.sizeSelector.setAttribute('data-size', evt.currentTarget.dataset.sizeLabel);
  }

  findIndex(array, value) {
    for (let i = 0; i < array?.length; i++) {
      if (this.normalizeSizeValue(array[i]) === this.normalizeSizeValue(value)) {
        return i;
      }
    }
    return -1; // If value is not found
  }

  normalizeSizeValue(value) {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim().toLowerCase();
  }

  getInputByEuValue(euValue) {
    const inputs = this.sizeSelector.querySelectorAll('input');
    return Array.from(inputs).find(
      (input) => this.normalizeSizeValue(input.value) === this.normalizeSizeValue(euValue)
    );
  }

  getAliasElement(input, unit) {
    if (!input) {
      return null;
    }

    const inputId = input.getAttribute('id');
    if (inputId) {
      const byOptionInput = this.sizeSelector.querySelector(`[data-${unit}][data-option-input="${inputId}"]`);
      if (byOptionInput) {
        return byOptionInput;
      }

      const byFor = this.sizeSelector.querySelector(`[data-${unit}][for="${inputId}"]`);
      if (byFor) {
        return byFor;
      }
    }

    // Fallback for structures where alias sizes are rendered as immediate siblings of the EU option.
    let sibling = input.nextElementSibling;
    while (sibling && sibling.tagName !== 'INPUT') {
      if (sibling.matches && sibling.matches(`[data-${unit}]`)) {
        return sibling;
      }
      sibling = sibling.nextElementSibling;
    }

    return null;
  }

  findFirstConversionValue(euValue, unitValues) {
    if (!unitValues || !unitValues.length || !this.euVals || !this.euVals.length) {
      return null;
    }

    let fallback = null;
    for (let i = 0; i < this.euVals.length; i += 1) {
      if (this.normalizeSizeValue(this.euVals[i]) !== this.normalizeSizeValue(euValue)) {
        continue;
      }

      const currentValue = unitValues[i];
      if (currentValue === undefined || currentValue === null) {
        continue;
      }

      const normalized = this.normalizeSizeValue(currentValue);
      if (normalized !== '') {
        return currentValue;
      }

      if (fallback === null) {
        fallback = currentValue;
      }
    }

    return fallback;
  }
}

class SizeSwatch extends HTMLElement {
  connectedCallback() {
    this.sizeChart = new SizeChart(this.querySelector('[data-sizechart-vals]'), this);

    if (this.sizeChart) {
      this.sizeChart?.initSizeChart();
    }
  }
}
window.customElements.define('size-swatch', SizeSwatch);

class LocationsGrid extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.locationsList = JSON.parse(this.dataset.locations);
    this.locationChildren = this.querySelectorAll('.location--image:not(.online-availibility)');
    if (this.locationsList) {
      document.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      console.log(this.locationsList);
    }
    this.onlineAvailibility = this.querySelector('.online-availibility');
    if (this.onlineAvailibility) {
      this.onlineAvailibility.classList.remove('available');
      this.onlineAvailibility.classList.add('unavailable');
      this.instoreOnly = this.onlineAvailibility.classList.contains('instore-only');
    }

    if (this.hasAttribute('data-current')) {
      this.initLocation(this.locationsList.locations, parseInt(this.getAttribute('data-current')));
    }
  }

  findLocationById(locations, id) {
    for (const location of locations) {
      const locationId = Object.keys(location)[0];
      if (parseInt(locationId) === id) {
        return location[locationId];
      }
    }
    return null; // Return null if location with given ID is not found
  }

  initLocation(locations, id) {
    let variantId = id;
    this.hideAllStores();
    let currentLocation = this.findLocationById(this.locationsList.locations, variantId);
    this.hideAllStores();
    if (this.onlineAvailibility) {
      this.onlineAvailibility.classList.remove('available');
      this.onlineAvailibility.classList.add('unavailable');
    }

    if (!currentLocation) {
      return;
    }

    currentLocation.some((item, index) => {
      if (item === 'Overkill Lager') {
        currentLocation.splice(index, 1);
        return true;
      }
    });

    if (this.onlineAvailibility && this.instoreOnly === false) {
      this.onlineAvailibility.classList.remove('unavailable');
      this.onlineAvailibility.classList.add('available');
    }

    console.log(currentLocation);

    this.locationChildren.forEach((image) => {
      if (currentLocation.includes(image.getAttribute('data-location'))) {
        image.classList.remove('unavailable');
      }
    });
  }

  handleVariantChange(event) {
    console.log(event);
    const { variant } = event.detail;
    let variantId = variant.id;
    this.hideAllStores();
    let currentLocation = this.findLocationById(this.locationsList.locations, variantId);
    console.log(currentLocation);
    this.hideAllStores();
    if (this.onlineAvailibility) {
      this.onlineAvailibility.classList.remove('available');
      this.onlineAvailibility.classList.add('unavailable');
    }

    if (!currentLocation) {
      return;
    }

    currentLocation.some((item, index) => {
      if (item === 'Overkill Lager') {
        currentLocation.splice(index, 1);
        return true;
      }
    });

    if (this.onlineAvailibility && this.instoreOnly === false) {
      this.onlineAvailibility.classList.remove('unavailable');
      this.onlineAvailibility.classList.add('available');
    }

    console.log(currentLocation);

    this.locationChildren.forEach((image) => {
      if (currentLocation.includes(image.getAttribute('data-location'))) {
        image.classList.remove('unavailable');
      }
    });
  }

  hideAllStores() {
    this.locationChildren.forEach((node) => {
      node.classList.add('unavailable');
    });
  }
}
window.customElements.define('locations-grid', LocationsGrid);

class ReadMore extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(e) {
    console.log(e, this.closest('disclosure__content'));
    if (this.closest('.disclosure__content')) {
      this.closest('.disclosure__content').classList.toggle('expanded');
    }

    if (this.closest('.product-description')) {
      this.closest('.product-description').classList.toggle('expanded');
    }
  }
}

window.customElements.define('read-more', ReadMore);

function getStyleDifferences(id1, id2) {
  let element1 = document.getElementById(id1);
  let element2 = document.getElementById(id2);

  const styles1 = window.getComputedStyle(element1);
  const styles2 = window.getComputedStyle(element2);

  const differences = {};
  for (let i = 0; i < styles1.length; i++) {
    const property = styles1[i];
    const value1 = styles1.getPropertyValue(property);
    const value2 = styles2.getPropertyValue(property);

    if (value1 !== value2) {
      differences[property] = { element1: value1, element2: value2 };
    }
  }

  for (const [property, values] of Object.entries(differences)) {
    console.log(`Property: ${property}, Element1: ${values.element1}, Element2: ${values.element2}`);
  }

  return differences;
}

if (!customElements.get('product-block-template')) {
  // Defer card fetches until their section nears the viewport. The section is observed rather than
  // the card itself, so off-screen carousel slides load together with the visible ones.
  const pendingTemplates = new Map();
  const templateObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              templateObserver.unobserve(entry.target);
              const templates = pendingTemplates.get(entry.target) || [];
              pendingTemplates.delete(entry.target);
              templates.forEach((template) => template.loadProductCard());
            });
          },
          { rootMargin: '600px 0px' }
        )
      : null;

  class ProductBlockTemplate extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (!templateObserver) {
        this.loadProductCard();
        return;
      }
      const target = this.closest('.shopify-section') || this.parentElement || this;
      if (!pendingTemplates.has(target)) {
        pendingTemplates.set(target, []);
        templateObserver.observe(target);
      }
      pendingTemplates.get(target).push(this);
    }

    async loadProductCard() {
      if (this.loading || !this.isConnected) return;
      this.loading = true;
      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('sections', 'product-block-render');
        if (this.dataset.grid) {
          url.searchParams.set('grid', this.dataset.grid);
        }
        const response = await fetch(url);
        if (!response.ok) {
          console.error('Failed to fetch section content');
          return false;
        }
        const content = await response.json();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content['product-block-render'];
        this.replaceWith(tempDiv.querySelector('product-block'));
      } catch (error) {
        console.error('Error fetching product card:', error);
      }
    }
  }
  customElements.define('product-block-template', ProductBlockTemplate);
}
class SizeLoader extends HTMLElement {
  constructor() {
    super();
    this.url = this.getAttribute('data-url');
  }

  log(input) {
    // console.log(`[SizeLoader] ${input}`);
  }

  connectedCallback() {
    this.log('SizeLoader Ready');
    // The swatches are only shown on hover/focus, so fetch them on first intent instead of on page load.
    this.trigger = this.closest('product-block') || this;
    this.onIntent = () => this.loadSwatch();
    ['pointerenter', 'focusin'].forEach((type) =>
      this.trigger.addEventListener(type, this.onIntent, { once: true, passive: true })
    );
  }

  disconnectedCallback() {
    ['pointerenter', 'focusin'].forEach((type) =>
      this.trigger?.removeEventListener(type, this.onIntent)
    );
  }

  async loadSwatch() {
    if (this.loaded) return;
    this.loaded = true;
    this.disconnectedCallback();
    this.log(this.url);

    try {
      const url = new URL(this.url, window.location.origin);
      url.searchParams.append('sections', 'size-chart-content');
      let contentResponse = await fetch(url);
      if (contentResponse.ok) {
        let content = await contentResponse.json();
        let tempDiv = document.createElement('div');
        tempDiv.innerHTML = content['size-chart-content'];
        this.appendChild(tempDiv.querySelector('size-swatch'));
      }
    } catch (error) {
      console.error(error);
    }
  }
}
window.customElements.define('size-loader', SizeLoader);

/* Payment Icons */
let swiperLibraryPromise = null;
function loadSwiperLibrary() {
  if (window.Swiper) {
    return Promise.resolve();
  }
  if (swiperLibraryPromise) {
    return swiperLibraryPromise;
  }
  swiperLibraryPromise = new Promise((resolve, reject) => {
    const assets = (window.theme && window.theme.assets) || {};
    if (!document.querySelector('link[data-swiper-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = assets.swiperCss;
      link.setAttribute('data-swiper-css', '');
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = assets.swiperJs;
    script.onload = () => resolve();
    script.onerror = () => {
      swiperLibraryPromise = null;
      reject(new Error('Failed to load Swiper library'));
    };
    document.head.appendChild(script);
  });
  return swiperLibraryPromise;
}

if (!customElements.get('payment-icons')) {
  class PaymentIconsSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.isLoadingSlider = false;
      this.paymentIconsWrapper = this.querySelector('.payment-icons--wrapper');
      this.swiperWrapper = this.paymentIconsWrapper.querySelector('.payment-icons--slider');
      this.swiperContainer = this.paymentIconsWrapper.querySelector('.payment-icons--slider-wrapper');
      this.swiperSlides = this.paymentIconsWrapper.querySelectorAll('.payment-icons--slider-item');
      this.sliderEnabled = this.getAttribute('data-slider-enabled') === 'true';
      this.autoPlayDelay = this.getAttribute('data-slider-autoplay-delay');

      this.handleResize = this.initSlider.bind(this);
    }

    connectedCallback() {
      if (this.sliderEnabled) {
        this.initSlider();
      }

      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
    }

    initSlider() {
      if (window.innerWidth >= 768 && this.slider === null && !this.isLoadingSlider) {
        this.isLoadingSlider = true;
        loadSwiperLibrary()
          .then(() => this.createSlider())
          .catch((error) => console.error(error))
          .finally(() => {
            this.isLoadingSlider = false;
          });
      } else if (window.innerWidth < 768 && this.slider !== null) {
        this.destroySlider();
      }
    }

    createSlider() {
      // Bail out if conditions changed while the library was loading
      if (window.innerWidth < 768 || this.slider !== null) {
        return;
      }

      // Add Swiper classes
      this.paymentIconsWrapper.classList.add('swiper-initialized');
      this.swiperWrapper.classList.add('swiper');
      this.swiperContainer.classList.add('swiper-wrapper');
      this.swiperSlides.forEach((slide) => slide.classList.add('swiper-slide'));

      const configs = {
        slidesPerView: 1,
        spaceBetween: 10,
        loop: true,
        navigation: {
          nextEl: '.swiper-button-next-custom',
          prevEl: '.swiper-button-prev-custom',
        },
        pagination: {
          el: '.swiper-pagination',
        },
        breakpoints: {
          830: {
            slidesPerView: 2,
          },
          930: {
            slidesPerView: 3,
          },
          999: {
            slidesPerView: 4,
          },
          1040: {
            slidesPerView: 1,
          },
          1130: {
            slidesPerView: 2,
          },
          1200: {
            slidesPerView: 3,
          },
          1380: {
            slidesPerView: 4,
          },
          1450: {
            slidesPerView: 5,
          },
        },
      };

      if (this.sliderEnabled) {
        configs['autoplay'] = {
          delay: this.autoPlayDelay ? parseInt(this.autoPlayDelay) : 3000,
        };
      }
      this.slider = new window.Swiper(this.swiperWrapper, configs);
    }

    destroySlider() {
      this.slider.destroy(true, true);
      this.slider = null;

      // Remove Swiper classes
      this.paymentIconsWrapper.classList.remove('swiper-initialized');
      this.swiperWrapper.classList.remove('swiper');
      this.swiperContainer.classList.remove('swiper-wrapper');
      this.swiperSlides.forEach((slide) => slide.classList.remove('swiper-slide'));
    }
  }
  customElements.define('payment-icons', PaymentIconsSlider);
}
